/**
 * EPIC-DM01: Demo Landing Page
 * Entry point for demo mode - start new demos, select events, view quota
 * 
 * Design Reference: Figma Demo Landing (iPad + phone)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemoMode, type DemoEvent } from '@/hooks/useDemoMode';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { AlertCircle, Play, Plus, Users, Loader2 } from 'lucide-react';

export default function DemoLanding() {
  const navigate = useNavigate();
  const {
    config,
    events,
    quota,
    user,
    isLoading,
    error,
    canAccessDemoMode,
    isDemoModeEnabled,
    createSession,
    isCreatingSession,
    createEvent,
    isCreatingEvent,
  } = useDemoMode();

  const NO_EVENT_VALUE = '__none__';
  const [selectedEventId, setSelectedEventId] = useState<string>(NO_EVENT_VALUE);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventLocation, setNewEventLocation] = useState('');

  // Handle starting a new demo
  const handleStartDemo = async () => {
    try {
      const eventId = selectedEventId === NO_EVENT_VALUE ? undefined : selectedEventId;
      const result = await createSession(eventId);
      // Navigate to intake page with the new session
      navigate(`/demo/intake/${result.session.id}`);
    } catch (err) {
      console.error('Failed to start demo:', err);
    }
  };

  // Handle creating a new event
  const handleCreateEvent = async () => {
    if (!newEventName.trim()) return;
    
    try {
      const newEvent = await createEvent({
        name: newEventName.trim(),
        location: newEventLocation.trim() || undefined,
      });
      setSelectedEventId(newEvent.id);
      setShowCreateEvent(false);
      setNewEventName('');
      setNewEventLocation('');
    } catch (err) {
      console.error('Failed to create event:', err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading Demo Mode...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {error instanceof Error ? error.message : 'Unable to access Demo Mode. Please ensure you have the required permissions.'}
            </p>
            <Button 
              className="mt-4 w-full" 
              variant="outline"
              onClick={() => navigate('/home')}
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not authorized
  if (!canAccessDemoMode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Not Authorized
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You do not have permission to access Demo Mode. Only admins and demo hosts can use this feature.
            </p>
            <Button 
              className="mt-4 w-full" 
              variant="outline"
              onClick={() => navigate('/home')}
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Demo mode disabled
  if (!isDemoModeEnabled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Demo Mode Disabled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Demo Mode is currently disabled. Please contact an administrator to enable it.
            </p>
            <Button 
              className="mt-4 w-full" 
              variant="outline"
              onClick={() => navigate('/home')}
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const quotaExhausted = quota && quota.remaining <= 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Demo Mode</h1>
            <p className="text-sm text-muted-foreground">
              {user?.isAdmin ? 'Admin' : 'Demo Host'}
            </p>
          </div>
          
          {/* Quota Display */}
          {quota && (
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Today's Demos</div>
              <div className={`text-2xl font-bold ${quotaExhausted ? 'text-destructive' : 'text-primary'}`}>
                {quota.used} / {quota.limit}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Start Demo Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Start New Demo
            </CardTitle>
            <CardDescription>
              Begin a swing analysis demo for a prospect
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Event Selector */}
            <div className="space-y-2">
              <Label htmlFor="event-select">Event (Optional)</Label>
              <div className="flex gap-2">
                <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                  <SelectTrigger id="event-select" className="flex-1">
                    <SelectValue placeholder="Select an event or leave empty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_EVENT_VALUE}>No Event</SelectItem>
                    {events.map((event: DemoEvent) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name}
                        {event.location && ` - ${event.location}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Create Event Button */}
                <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Event</DialogTitle>
                      <DialogDescription>
                        Create a new event to group demo sessions
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="event-name">Event Name *</Label>
                        <Input
                          id="event-name"
                          placeholder="e.g., PGA Show 2026"
                          value={newEventName}
                          onChange={(e) => setNewEventName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="event-location">Location (Optional)</Label>
                        <Input
                          id="event-location"
                          placeholder="e.g., Orlando, FL"
                          value={newEventLocation}
                          onChange={(e) => setNewEventLocation(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setShowCreateEvent(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateEvent}
                        disabled={!newEventName.trim() || isCreatingEvent}
                      >
                        {isCreatingEvent ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          'Create Event'
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Quota Warning */}
            {quotaExhausted && (
              <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Daily limit reached</p>
                  <p className="text-destructive/80">
                    You have used all {quota?.limit} demos for today. 
                    Please try again tomorrow.
                  </p>
                </div>
              </div>
            )}

            {/* Start Demo Button */}
            <Button
              size="lg"
              className="w-full h-14 text-lg"
              onClick={handleStartDemo}
              disabled={quotaExhausted || isCreatingSession}
            >
              {isCreatingSession ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Starting Demo...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-5 w-5" />
                  Start New Demo
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Session Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-primary">{quota?.used ?? 0}</div>
                <div className="text-sm text-muted-foreground">Today</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary">{quota?.remaining ?? 0}</div>
                <div className="text-sm text-muted-foreground">Remaining</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary">{config?.maxSwings ?? 3}</div>
                <div className="text-sm text-muted-foreground">Max Swings</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
